import { useCallback, useEffect, useState } from 'react';
import { fetchProfile, patchProfile } from '../api/profileApi';
import { AssetRequestError } from '../api/getAssetById';
import { useI18n } from '../i18n';
import {
  extractProfileVehicles,
  formatProfileVehicleSummaryLine,
  normalizeProfileVehiclesPrimary,
  profileVehiclesToWire,
  validateProfileVehicles,
  type ProfileVehicle,
} from '../utils/profileVehicles';
import { resolveProfileRecord } from '../utils/profileDisplaySummary';
import { assertProfileMutationSuccess } from '../utils/profileWorkerList';
import { ProfileVehiclesEditSection } from './ProfileVehiclesEditSection';
import styles from './BookingParkingVehiclePanel.module.css';

type BookingParkingVehiclePanelProps = {
  /** Pre-selected vehicle id from a prior step (optional). */
  initialVehicleId?: string | null;
  onContinue: (vehicle: ProfileVehicle) => void;
  onCancel: () => void;
};

export function BookingParkingVehiclePanel({
  initialVehicleId = null,
  onContinue,
  onCancel,
}: BookingParkingVehiclePanelProps) {
  const { t, language } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<ProfileVehicle[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(initialVehicleId);
  const [editing, setEditing] = useState(false);
  const [draftVehicles, setDraftVehicles] = useState<ProfileVehicle[]>([]);

  const loadVehicles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProfile();
      const list = extractProfileVehicles(resolveProfileRecord(data));
      setVehicles(list);
      setDraftVehicles(list);
      setSelectedId((prev) => {
        if (prev && list.some((car) => car.id === prev)) {
          return prev;
        }
        const primary = list.find((car) => car.isPrimary);
        return primary?.id ?? list[0]?.id ?? null;
      });
      if (list.length === 0) {
        setEditing(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.somethingWrong'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadVehicles();
  }, [loadVehicles]);

  const onSaveCars = async () => {
    const normalized = normalizeProfileVehiclesPrimary(draftVehicles);
    const issue = validateProfileVehicles(normalized);
    if (issue) {
      const messageKey =
        issue === 'max'
          ? 'profile.vehicles.errorMax'
          : issue === 'license'
            ? 'profile.vehicles.errorLicense'
            : issue === 'duplicateLicense'
              ? 'profile.vehicles.errorDuplicateLicense'
              : issue === 'primary'
                ? 'profile.vehicles.errorPrimary'
                : 'profile.vehicles.errorIncomplete';
      setError(t(messageKey));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const response = await patchProfile({ vehicles: profileVehiclesToWire(normalized) });
      assertProfileMutationSuccess(response, t('profile.operation.patchProfile'));
      await loadVehicles();
      setEditing(false);
    } catch (err) {
      setError(
        err instanceof AssetRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('common.somethingWrong'),
      );
    } finally {
      setSaving(false);
    }
  };

  const selected = vehicles.find((car) => car.id === selectedId) ?? null;
  const canContinue =
    Boolean(selected) &&
    !selected!.id.startsWith('local-') &&
    !loading &&
    !saving &&
    !editing;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('bookings.parkingVehicle.title')}
      onClick={onCancel}
    >
      <section
        className={styles.sheet}
        dir={language === 'he' ? 'rtl' : 'ltr'}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.headerRow}>
          <h2 className={styles.title}>
            {editing ? t('bookings.parkingVehicle.editList') : t('bookings.parkingVehicle.title')}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => {
              if (editing && vehicles.length > 0) {
                setDraftVehicles(vehicles);
                setEditing(false);
                setError(null);
                return;
              }
              onCancel();
            }}
            aria-label={
              editing ? t('bookings.parkingVehicle.backToList') : t('bookings.parkingVehicle.cancel')
            }
          >
            ×
          </button>
        </div>
        {!editing ? <p className={styles.intro}>{t('bookings.parkingVehicle.intro')}</p> : null}

        {loading ? (
          <p className={styles.status} role="status">
            {t('common.loading')}
          </p>
        ) : null}

        {!loading && !editing ? (
          <>
            {vehicles.length === 0 ? (
              <p className={styles.status}>{t('bookings.parkingVehicle.none')}</p>
            ) : (
              <ul className={styles.list}>
                {vehicles.map((car) => {
                  const line = formatProfileVehicleSummaryLine(car);
                  return (
                    <li key={car.id}>
                      <label className={styles.carOption}>
                        <input
                          type="radio"
                          name="parking-booking-vehicle"
                          checked={selectedId === car.id}
                          onChange={() => setSelectedId(car.id)}
                          disabled={saving}
                        />
                        <span className={styles.carBody}>
                          <span className={styles.carLine} dir="ltr">
                            {line}
                          </span>
                          {car.isPrimary ? (
                            <span className={styles.primaryBadge}>
                              {t('profile.vehicles.primary')}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={!canContinue}
                onClick={() => {
                  if (selected && !selected.id.startsWith('local-')) {
                    onContinue(selected);
                  }
                }}
              >
                {selected?.isPrimary
                  ? t('bookings.parkingVehicle.continuePrimary')
                  : t('bookings.parkingVehicle.continueSelected')}
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={saving}
                onClick={() => {
                  setDraftVehicles(vehicles);
                  setEditing(true);
                  setError(null);
                }}
              >
                {t('bookings.parkingVehicle.editList')}
              </button>
              <button type="button" className={styles.ghostBtn} onClick={onCancel} disabled={saving}>
                {t('bookings.parkingVehicle.cancel')}
              </button>
            </div>
          </>
        ) : null}

        {!loading && editing ? (
          <>
            <ProfileVehiclesEditSection
              vehicles={draftVehicles}
              onChange={setDraftVehicles}
              disabled={saving}
              onCancel={() => {
                setDraftVehicles(vehicles);
                setEditing(false);
                setError(null);
              }}
              cancelAriaLabel={t('bookings.parkingVehicle.backToList')}
            />
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={saving}
                onClick={() => void onSaveCars()}
              >
                {saving ? t('common.submitting') : t('bookings.parkingVehicle.saveCars')}
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={saving}
                onClick={() => {
                  setDraftVehicles(vehicles);
                  setEditing(false);
                  setError(null);
                }}
              >
                {t('bookings.parkingVehicle.backToList')}
              </button>
            </div>
          </>
        ) : null}

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
      </section>
    </div>
  );
}
